import { z } from "zod";

export const REGULATORY_JUSTIFICATION_MAX_LENGTH = 300;

const locationDraftSchema = z.object({
  codUnidade: z.number(),
  nome: z.string(),
  endereco: z.string(),
});

export const regulatoryDocumentSchema = z.object({
  codTipoDocumentoRegulatorio: z.number().positive(),
  documentName: z.string().min(1),
  codMotivoDocumento: z.number().positive(),
  reasonLabel: z.string().min(1),
  requiresJustification: z.boolean(),
  justification: z.string().max(REGULATORY_JUSTIFICATION_MAX_LENGTH),
  selectedLocations: z.array(locationDraftSchema),
  siglaUF: z.string(),
  stateLabel: z.string(),
  coveredLocations: z.array(z.string()),
  temEntradasDiferentes: z.boolean(),
  requiresLocationIn: z.array(z.string()),
});

export type RegulatoryDocumentDraft = z.infer<typeof regulatoryDocumentSchema>;
export type RegulatoryDocumentLocationDraft = z.infer<typeof locationDraftSchema>;

const documentStepSchema = regulatoryDocumentSchema.pick({
  codTipoDocumentoRegulatorio: true,
  documentName: true,
});

export const isDocumentStepComplete = (draft: RegulatoryDocumentDraft): boolean =>
  documentStepSchema.safeParse(draft).success;

const reasonStepSchema = regulatoryDocumentSchema
  .pick({
    codTipoDocumentoRegulatorio: true,
    documentName: true,
    codMotivoDocumento: true,
    reasonLabel: true,
    requiresJustification: true,
    justification: true,
  })
  .refine(
    (draft) => !draft.requiresJustification || draft.justification.trim().length > 0,
  );

export const isReasonStepComplete = (draft: RegulatoryDocumentDraft): boolean =>
  reasonStepSchema.safeParse(draft).success;

export const requiresLocationChoice = (draft: RegulatoryDocumentDraft): boolean =>
  Boolean(draft.siglaUF) && draft.requiresLocationIn.includes(draft.siglaUF);

export const isLocationStepComplete = (draft: RegulatoryDocumentDraft): boolean =>
  !requiresLocationChoice(draft) || draft.selectedLocations.length > 0;
